import React, { useState, useEffect } from 'react';

// ✅ WICHTIG: Kein Port :443 verwenden! Verursacht Probleme bei Image-Loading
const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

/**
 * 🔧 FIX: Konvertiere /uploads/early-access/ URLs zu /api/early-access/serve-image/
 * Nginx blockiert direkten Zugriff auf /uploads/, daher müssen wir den Spring Boot Endpoint verwenden
 * 
 * 👁️ WICHTIG: Diese Funktion ist NUR für die ANZEIGE (Preview)!
 * Die ORIGINALE URL wird an onUploadSuccess übergeben für die Speicherung!
 */
const convertToServeImageUrl = (url) => {
  if (!url) return '';
  
  // Wenn es bereits eine serve-image URL ist, nicht ändern
  if (url.includes('/api/early-access/serve-image/')) {
    return url;
  }
  
  // Konvertiere /uploads/early-access/filename.jpg zu /api/early-access/serve-image/filename.jpg
  // NUR FÜR DISPLAY - nicht für Speicherung!
  if (url.includes('/uploads/early-access/')) {
    const filename = url.split('/uploads/early-access/').pop();
    const newUrl = `${BASE_URL}/api/early-access/serve-image/${filename}`;
    console.log(`👁️ URL converted FOR DISPLAY ONLY: ${url} → ${newUrl}`);
    return newUrl;
  }
  
  // Falls nur der Filename vorhanden ist
  if (!url.startsWith('http') && !url.startsWith('/')) {
    const newUrl = `${BASE_URL}/api/early-access/serve-image/${url}`;
    console.log(`🔄 URL converted (filename only): ${url} → ${newUrl}`);
    return newUrl;
  }
  
  return url;
};

/**
 * Image/Video Upload Component for Early Access Content
 * Allows admins to upload images/videos and get the URL back
 * 
 * WICHTIG: Jede Instanz braucht eine eindeutige `type` prop!
 */
const MediaUploader = ({ token, onUploadSuccess, currentUrl = '', label = "Upload Media", type = "news" }) => {
  const [uploading, setUploading] = useState(false);
  // 🔧 FIX: Initial preview MUSS auch konvertiert werden!
  const [preview, setPreview] = useState(() => convertToServeImageUrl(currentUrl));
  const [originalUrl, setOriginalUrl] = useState(currentUrl); // 🆕 Original URL für Speicherung
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [updateKey, setUpdateKey] = useState(0); // 🆕 Force re-render key

  // 🆕 Reset state wenn sich currentUrl oder type ändert
  useEffect(() => {
    console.log(`🔄 MediaUploader [${type}] currentUrl changed:`, currentUrl);
    // 🔧 FIX: Konvertiere URL für Anzeige, behalte Original für Speicherung
    setOriginalUrl(currentUrl);
    const displayUrl = convertToServeImageUrl(currentUrl);
    console.log(`🔄 MediaUploader [${type}] display URL set to:`, displayUrl);
    setPreview(displayUrl);
    setFile(null);
    setError('');
    setUpdateKey(prev => prev + 1); // 🆕 Force re-render
    
    // Detect media type from URL
    if (currentUrl) {
      const isVideo = currentUrl.includes('.mp4') || currentUrl.includes('.webm') || currentUrl.includes('video');
      setMediaType(isVideo ? 'video' : 'image');
    }
  }, [currentUrl, type]);

  // 🔍 Debug: Preview State beobachten
  useEffect(() => {
    console.log(`👁️ MediaUploader [${type}] preview state changed (key: ${updateKey}):`, preview ? `${preview.substring(0, 80)}...` : 'empty');
  }, [preview, type, updateKey]);

  // Compress image before upload
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Max dimensions
          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1080;
          
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              }));
            },
            'image/jpeg',
            0.85 // 85% quality
          );
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    console.log(`📁 MediaUploader [${type}] file selected:`, selectedFile.name, selectedFile.type);
    
    try {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime'];
      if (!validTypes.includes(selectedFile.type)) {
        setError('Only images (JPEG, PNG, GIF, WebP) and videos (MP4, WebM, MOV) are allowed');
        return;
      }

      // Determine media type
      const isVideo = selectedFile.type.startsWith('video/');
      setMediaType(isVideo ? 'video' : 'image');

      // For images, compress before validation
      let processedFile = selectedFile;
      if (selectedFile.type.startsWith('image/')) {
        console.log(`🖼️ Original image size: ${(selectedFile.size / 1024).toFixed(2)} KB`);
        processedFile = await compressImage(selectedFile);
        console.log(`🖼️ Compressed size: ${(processedFile.size / 1024).toFixed(2)} KB`);
      }

      // Validate file size (max 5MB for images, 50MB for videos)
      const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
      if (processedFile.size > maxSize) {
        setError(`File size must be less than ${isVideo ? '50MB' : '5MB'}. Please choose a smaller file.`);
        return;
      }

      setFile(processedFile);
      setError('');
      
      // Create preview - use Data URL for local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        console.log(`✅ MediaUploader [${type}] preview set, data URL length: ${dataUrl?.length || 0}`);
        console.log(`✅ MediaUploader [${type}] data URL starts with: ${dataUrl?.substring(0, 50)}...`);
        setPreview(dataUrl);
      };
      reader.onerror = (err) => {
        console.error(`❌ MediaUploader [${type}] FileReader error:`, err);
        setError('Failed to read file for preview');
      };
      reader.readAsDataURL(processedFile);
      
    } catch (err) {
      console.error(`❌ MediaUploader [${type}] error processing file:`, err);
      setError('Failed to process file. Please try another one.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      formData.append('mediaType', mediaType); // 🆕 Sende mediaType mit

      console.log(`📤 MediaUploader [${type}] uploading: ${file.name}, size: ${(file.size / 1024).toFixed(2)} KB, mediaType: ${mediaType}`);

      // WICHTIG: Verwende fetch() wie in CampOverview.js, NICHT axios!
      const response = await fetch(`${BASE_URL}/api/early-access/upload-media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // DO NOT set Content-Type for FormData - Browser setzt es automatisch mit boundary
        },
        body: formData
      });

      console.log(`📤 MediaUploader [${type}] response status:`, response.status);

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ MediaUploader [${type}] upload successful:`, result);
        
        // Build full URL
        let fullUrl = result.url.startsWith('http') 
          ? result.url 
          : `${BASE_URL}${result.url}`;
        
        // ✅ KRITISCH: Entferne :443 aus der URL (verursacht Probleme bei mobilen Apps)
        fullUrl = fullUrl.replace(':443/', '/').replace(':443', '');
          
        console.log(`✅ MediaUploader [${type}] cleaned URL:`, fullUrl);
        
        // 🔧 FIX: Speichere Original-URL, aber zeige konvertierte URL für Preview
        setOriginalUrl(fullUrl);
        setPreview(convertToServeImageUrl(fullUrl));
        
        // 🆕 Gib den korrekten mediaType zurück
        const returnMediaType = result.type || mediaType || 'image';
        
        // 💾 WICHTIG: Gib die ORIGINALE URL (nicht konvertierte!) an den Callback!
        console.log(`💾 MediaUploader [${type}] calling onUploadSuccess with ORIGINAL URL:`, fullUrl);
        onUploadSuccess(fullUrl, returnMediaType);
        setFile(null);
      } else {
        const errorText = await response.text();
        console.error(`❌ MediaUploader [${type}] upload error:`, errorText);
        
        let errorMessage = 'Failed to upload file';
        if (response.status === 413) {
          errorMessage = 'File is too large. Please compress the file or choose a smaller one.';
        } else if (response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Upload failed: ${response.status} - ${errorText}`;
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('❌ Upload network error:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    console.log(`🗑️ MediaUploader [${type}] removing media`);
    setFile(null);
    setPreview('');
    setOriginalUrl('');
    setMediaType('image');
    onUploadSuccess('', '');
  };

  // Helper to check if preview is a video
  const isVideoPreview = () => {
    if (mediaType === 'video') return true;
    if (file?.type?.startsWith('video')) return true;
    if (preview && (preview.includes('.mp4') || preview.includes('.webm') || preview.includes('.mov') || preview.includes('video'))) return true;
    return false;
  };

  // Helper to add cache buster to URL to force reload
  const getPreviewUrlWithCacheBuster = () => {
    if (!preview) return '';
    // Don't add cache buster to data URLs
    if (preview.startsWith('data:')) return preview;
    // Add timestamp to force browser to reload image
    const separator = preview.includes('?') ? '&' : '?';
    return `${preview}${separator}_t=${updateKey}`;
  };

  return (
    <div style={styles.container}>
      <label style={styles.label}>
        {label}
        {mediaType === 'video' && <span style={styles.videoBadge}>🎬 Video</span>}
      </label>
      
      {/* Preview */}
      {preview && (
        <div style={styles.previewContainer}>
          {isVideoPreview() ? (
            <video src={getPreviewUrlWithCacheBuster()} style={styles.preview} controls playsInline key={`video-${updateKey}`} />
          ) : (
            <img 
              key={`img-${updateKey}`}
              src={getPreviewUrlWithCacheBuster()} 
              alt="Preview" 
              style={styles.preview} 
              onLoad={() => {
                console.log(`✅ MediaUploader [${type}] image loaded successfully (key: ${updateKey})`);
              }}
              onError={(e) => {
                console.error(`❌ MediaUploader [${type}] image failed to load:`, preview?.substring(0, 100));
                // Don't hide the image, show a placeholder instead
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"><rect fill="%23f0f0f0" width="200" height="150"/><text fill="%23999" font-family="Arial" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em">⚠️ Image Error</text></svg>';
              }} 
            />
          )}
          <button 
            onClick={handleRemove} 
            style={styles.removeButton}
            type="button"
          >
            ✕ Remove
          </button>
        </div>
      )}

      {/* Upload Controls */}
      <div style={styles.uploadControls}>
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
          onChange={handleFileChange}
          style={styles.fileInput}
          id={`file-upload-${type}`}
        />
        <label htmlFor={`file-upload-${type}`} style={styles.fileInputLabel}>
          📁 Choose Image or Video
        </label>
        
        {file && (
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{
              ...styles.uploadButton,
              ...(uploading ? styles.uploadButtonDisabled : {})
            }}
            type="button"
          >
            {uploading ? '⏳ Uploading...' : '⬆️ Upload'}
          </button>
        )}
      </div>

      {/* File Info */}
      {file && (
        <div style={styles.fileInfo}>
          📄 {file.name} ({(file.size / 1024).toFixed(2)} KB)
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={styles.error}>
          ⚠️ {error}
        </div>
      )}

      {/* Current URL (readonly) - zeigt Original URL für Debugging */}
      {(preview || originalUrl) && (
        <div style={styles.urlDisplay}>
          <small style={styles.urlLabel}>Stored URL:</small>
          <input
            type="text"
            value={originalUrl}
            readOnly
            style={styles.urlInput}
          />
          <small style={{...styles.urlLabel, marginTop: '5px', color: '#4CAF50'}}>Display URL (via serve-image):</small>
          <input
            type="text"
            value={preview}
            readOnly
            style={{...styles.urlInput, backgroundColor: '#e8f5e9'}}
          />
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
    marginBottom: '8px'
  },
  previewContainer: {
    position: 'relative',
    marginBottom: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    padding: '10px',
    backgroundColor: '#f9f9f9'
  },
  preview: {
    width: '100%',
    maxHeight: '300px',
    objectFit: 'contain',
    borderRadius: '6px'
  },
  removeButton: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    padding: '8px 12px',
    backgroundColor: '#ff4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600'
  },
  uploadControls: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    marginBottom: '10px'
  },
  fileInput: {
    display: 'none'
  },
  fileInputLabel: {
    padding: '10px 16px',
    backgroundColor: '#f0f0f0',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.3s'
  },
  uploadButton: {
    padding: '10px 16px',
    backgroundColor: '#da291c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.3s'
  },
  uploadButtonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  fileInfo: {
    padding: '8px 12px',
    backgroundColor: '#e8f5e9',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#2e7d32',
    marginBottom: '10px'
  },
  error: {
    padding: '10px 12px',
    backgroundColor: '#ffebee',
    color: '#c62828',
    borderRadius: '6px',
    fontSize: '13px',
    marginTop: '10px'
  },
  urlDisplay: {
    marginTop: '10px'
  },
  urlLabel: {
    display: 'block',
    fontSize: '12px',
    color: '#888',
    marginBottom: '5px'
  },
  urlInput: {
    width: '100%',
    padding: '8px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    backgroundColor: '#f9f9f9',
    color: '#666'
  },
  videoBadge: {
    marginLeft: '8px',
    padding: '2px 8px',
    backgroundColor: '#9c27b0',
    color: 'white',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600'
  }
};

export default MediaUploader;
